"use client";

import React, { useState, useEffect, useRef } from "react";
import { ArrowRight, ChartBar, TrendingUp, ShoppingBasket, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
export default function Hero() {
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const containerRef = useRef<HTMLElement>(null);
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setMousePos({
      x,
      y
    });
  };
  const parallaxStyle = (depth: number) => ({
    transform: `translate(${mousePos.x * depth * 40}px, ${mousePos.y * depth * 40}px)`,
    transition: "transform 0.1s ease-out"
  });
  return <section id="hero" ref={containerRef} onMouseMove={handleMouseMove} className="relative min-h-[90vh] flex items-center overflow-hidden bg-background pt-20 pb-16 lg:pt-32 lg:pb-24">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(var(--foreground) 1px, transparent 1px)`,
        backgroundSize: "24px 24px"
      }} />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-[60%_40%] gap-12 lg:gap-20 items-center">
          {}
          <div className="flex flex-col gap-8 max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/50 border border-accent text-accent-foreground w-fit text-sm font-medium">
              <TrendingUp className="w-4 h-4" />
              <span>Эффективность, подтвержденная данными</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-foreground">
              Превращаем розницу в{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[var(--primary)] to-[var(--chart-5)]">
                прибыль
              </span>
            </h1>

            <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">Агентство ПРОФИ объединяет аналитическую точность и креативный подход. Мы разрабатываем стратегии мерчандайзинга, которые повышают узнаваемость бренда и стимулируют продажи в каждой торговой точке</p>

            <div className="flex flex-wrap gap-4 mt-2">
              <Button asChild size="lg" className="text-base h-12 px-8 shadow-lg shadow-primary/20">
                <Link to="#contact">
                  Начать продвижение
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="ghost" size="lg" className="text-base h-12 px-8 border border-border bg-background/50 backdrop-blur-sm hover:bg-accent/10">
                <Link to="#services-offered">Наши услуги</Link>
              </Button>
            </div>

            {}
            <div className="pt-8 border-t border-border mt-4 flex flex-wrap gap-8 text-sm text-muted-foreground font-medium">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-primary" />
                Работаем с крупными сетями
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-chart-2" />
                Аналитика продаж
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-chart-4" />
                Охват по всей России
              </div>
            </div>
          </div>

          {}
          <div className="relative hidden lg:block [perspective:1000px]">
            <div className="relative w-full aspect-square max-w-[600px] mx-auto">
              
              {}
              <div className="absolute inset-0 bg-gradient-to-tr from-accent/20 to-transparent rounded-3xl transform -rotate-6 scale-95 border border-border/50" style={parallaxStyle(-0.5)} />

              {}
              <div className="absolute inset-4 z-10 rounded-2xl overflow-hidden shadow-2xl border border-border bg-card/50 backdrop-blur-sm" style={parallaxStyle(0.2)}>
                <AspectRatio ratio={1}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1567030/img/retail_analytics_dashboard_glass.png" alt="Панель аналитики розничных продаж" className="object-cover w-full h-full opacity-90 hover:scale-105 transition-transform duration-700" />
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </AspectRatio>
              </div>

              {}
              
              {}
              <div className="absolute -top-6 -right-6 z-20 p-4 rounded-xl bg-card border border-border shadow-xl backdrop-blur-md w-48" style={parallaxStyle(0.8)}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <ChartBar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">Рост продаж</span>
                </div>
                <div className="text-2xl font-bold text-foreground">+24%</div>
                <div className="text-xs text-muted-foreground">за последний квартал</div>
              </div>

              {}
              <div className="absolute -bottom-8 -left-4 z-30 p-4 rounded-xl bg-card border border-border shadow-xl backdrop-blur-md w-56" style={parallaxStyle(1.2)}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-chart-2/10 text-chart-2">
                    <ShoppingBasket className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-semibold text-foreground">Эффективность полки</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2 mb-2 overflow-hidden">
                  <div className="bg-chart-2 h-2 rounded-full w-[85%]" />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Текущая</span>
                  <span className="font-medium text-foreground">85%</span>
                </div>
              </div>

              {}
              <div className="absolute top-1/2 -right-12 z-20 p-3 rounded-lg bg-card/90 border border-border shadow-lg backdrop-blur-sm flex items-center gap-3" style={parallaxStyle(0.5)}>
                <div className="p-1.5 rounded bg-chart-4/10 text-chart-4">
                  <Layers className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-bold text-foreground">1,200+</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">SKU в работе</div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>;
}